import { useState } from "react";
import type React from "react";
import type { ProgressSession, ProgressSnapshot } from "../progress/session";
import { cloudConfigured } from "../app/session-factory";
import { Icon } from "./Icon";
import { Header, type NavigationProps } from "./SiteHeader";

export function AccountPage({
  session,
  progress,
  onNavigate,
  onBack,
  onRefresh,
}: NavigationProps & {
  readonly session: ProgressSession;
  readonly progress: ProgressSnapshot;
  readonly onBack: () => void;
  readonly onRefresh: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const login = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    await session.login(email, password);
    setEmail("");
    setPassword("");
    setBusy(false);
    onRefresh();
  };
  return (
    <>
      <Header onNavigate={onNavigate} progress={progress} active="account" />
      <main className="panel-page">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Optional sync</p>
            <h1>账户进度</h1>
            <p className="page-description">换一台设备，也能接续你的成长。</p>
          </div>
          <button className="ghost-button" onClick={onBack}>
            <Icon name="back" />
            返回
          </button>
        </div>
        <div className="account-layout">
          <section className="account-panel" aria-labelledby="account-title">
            <div className="section-heading">
              <Icon name="cloud" />
              <h2 id="account-title">
                {progress.user ? "你的账户进度" : "使用个人主页账户登录"}
              </h2>
            </div>
            <p className="dialog-copy">
              使用已有的个人主页账户，在 Gloamline
              单独登录。已获云存档授权的账户可以同步纪录、设置与外观；本站暂不提供公开注册。
            </p>
            {!cloudConfigured ? (
              <div className="save-notice">
                此版本暂未提供云存档。你仍可直接游玩，进度保存在当前浏览器。
              </div>
            ) : null}
            {progress.conflict ? (
              <div
                className="account-form"
                aria-label="Progress Conflict 解决方式"
              >
                <div className="save-notice">
                  此浏览器与云端都有新的进度。纪录、累计统计、教学进展与已解锁外观会安全合并；请选择设置和当前外观的来源。选择前不会覆盖云端。
                </div>
                <button
                  className="primary-button"
                  onClick={() =>
                    void session.resolveConflict("local").then(onRefresh)
                  }
                >
                  安全合并 · 使用此浏览器设置
                </button>
                <button
                  className="secondary-button"
                  onClick={() =>
                    void session.resolveConflict("cloud").then(onRefresh)
                  }
                >
                  安全合并 · 使用云端设置
                </button>
                <button
                  className="secondary-button"
                  onClick={() => {
                    session.deferConflict();
                    onRefresh();
                  }}
                >
                  暂缓 · 继续本地游玩
                </button>
              </div>
            ) : null}
            {progress.user ? (
              <div className="dialog-actions">
                <button
                  className="secondary-button"
                  disabled={busy}
                  onClick={() => {
                    setEmail("");
                    setPassword("");
                    setBusy(true);
                    void session.logout().finally(() => {
                      setBusy(false);
                      onRefresh();
                    });
                  }}
                >
                  {busy ? "正在安全退出…" : "退出并返回 Guest Progress"}
                </button>
              </div>
            ) : (
              <form
                className="account-form"
                onSubmit={(event) => void login(event)}
              >
                <div className="field">
                  <label htmlFor="email">账户邮箱</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    disabled={!cloudConfigured}
                  />
                </div>
                <div className="field">
                  <label htmlFor="password">密码</label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    disabled={!cloudConfigured}
                  />
                </div>
                <button
                  className="primary-button"
                  type="submit"
                  disabled={busy || !cloudConfigured}
                >
                  {busy ? "验证中…" : "登录并检查授权"}
                </button>
              </form>
            )}
            <div className="save-notice" aria-live="polite">
              {progress.message}
            </div>
          </section>
          <aside className="account-details" aria-label="账户与存档说明">
            <section className="help-section">
              <div className="section-heading">
                <Icon name="play" />
                <h2>随时开始，无需登录</h2>
              </div>
              <p>
                两种模式和全部外观都能通过游玩体验与解锁。云存档只是把已同步的账户进度带到其他设备。
              </p>
            </section>
            <section className="help-section">
              <div className="section-heading">
                <Icon name="shield" />
                <h2>每一份进度，分别保存</h2>
              </div>
              <p>
                游客进度只属于当前浏览器。账户进度单独保存，退出后会回到原来的游客进度。
              </p>
              <p>
                连接中断时仍可本地游玩；如果两处存档发生冲突，会先请你选择设置与外观的来源。
              </p>
            </section>
          </aside>
        </div>
      </main>
    </>
  );
}
